import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, throwError, EMPTY, from, of, defer } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';

import { AppConstant } from '../utility/app-constant';
import { LoginLogoutService } from './auth/login-logout.service';
import { TimeOutService } from './auth/time-out.service';
import { NotificationService } from './notification/notification.service';
import { HttpResponseMessage } from '../utility/http-response-message';
import { PrivilegeService } from './privilege.service';
import { UserService } from './user/user.service';
import { AuthService } from '@auth0/auth0-angular';
import { AppEnumConstants } from "../enums/app-enum-constants";
import { CommonUtility } from "../utility/common-utility";
import { AuthOktaService } from "./auth/auth-okta.service";

@Injectable({
  providedIn: 'root'
})
export class BasicAuthHttpInterceptorServiceService implements HttpInterceptor {
  private isHandlingDelicateError = false;
  private isRefreshingAuth0Token = false;
  private isLoggingOut = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private delicateErrorSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private statusToTrack = [AppConstant.BAD_REQUEST, AppConstant.NOT_FOUND, AppConstant.SERVER_TIME_OUT, AppConstant.PAPERTRL_SERVER_ERROR,
  AppConstant.FORBIDDEN, AppConstant.SERVER_DOWN, AppConstant.SERVER_NOT_FOUND];
  public commonUtil = new CommonUtility();

  constructor(private tokenRevalidateService: TimeOutService, public privilegeService: PrivilegeService,
    private auth0: AuthService, private loginLogOutService: LoginLogoutService, private authOktaService: AuthOktaService,
    public notificationService: NotificationService, private userService: UserService) {
  }

  /**
   * Detect if current session is Auth0 based.
   * You can set this flag in localStorage when user logs in via Auth0.
   */
  private isAuth0User(): boolean {
    return this.privilegeService.isAuth0Enabled();
  }

  /**
   * Check if the error is an Auth0 token error that requires logout
   * (e.g., missing refresh token, login required, consent required)
   */
  private isAuth0TokenError(error: any): boolean {
    const errorCode = error?.error || error?.message || '';
    const auth0TokenErrors = ['login_required', 'consent_required', 'missing_refresh_token', 'Missing Refresh Token'];
    return auth0TokenErrors.some(errorType =>
      String(errorCode).toLowerCase().includes(errorType.toLowerCase())
    );
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.privilegeService.networkStatus) {
      return throwError(() => 'Network not available');
    }

    if (this.isHandlingDelicateError && !req.url.includes('sec_get_user_details_and_nav_list')) {
      return EMPTY;
    }

    // ✅ Add breadcrumb for Sentry tracing
    Sentry.addBreadcrumb({
      category: 'http',
      message: `${req.method} ${req.url}`,
      level: 'info',
      data: { method: req.method, url: req.url }
    });

    // ✅ Skip authentication for public endpoints
    if (req.url.includes('email-verification-resend')) {
      return next.handle(req).pipe(catchError(err => this.handleError(err, req, next)));
    }

    // ✅ If Auth0 user, handle token asynchronously
    if (this.isAuth0User()) {
      return from(this.auth0.getAccessTokenSilently()).pipe(
        switchMap(token => {
          const authReq = token ? req.clone({ setHeaders: this.addAuth0Headers(token, req) }) : req;
          return next.handle(authReq);
        }),
        catchError(err => {
          // Handle Auth0 token errors specifically
          if (this.isAuth0TokenError(err)) {
            if (!this.isLoggingOut) {
              this.isLoggingOut = true;
              console.warn('Auth0 token expired or missing, logging out user', err);
              this.loginLogOutService.clearLocalStorage(true);
              this.authOktaService.signOut({ logoutParams: { returnTo: window.location.origin } });
            }
            return EMPTY;
          }
          return this.handleError(err, req, next);
        })
      );
    } else {
      // ✅ Otherwise, use legacy headers
      const modifiedReq = this.addLegacyHeaders(req);
      return next.handle(modifiedReq).pipe(catchError(err => this.handleError(err, req, next)));
    }
  }

  /**
   * Add Auth0 headers
   */
  addAuth0Headers(token, req) {
    const headers: { [name: string]: string } = {
      Authorization: `Bearer ${token}`,
      AuthenticationSource: 'auth0'
    };

    const subClientId = localStorage.getItem(AppConstant.SUB_CLIENT_ID);

    const user = JSON.parse(localStorage.getItem(AppConstant.SESSION_USER_ATTR) || '{}');
    if (user?.switchedToDelegateAccount && !req.url.includes('sec_get_user_details_and_nav_list') && !req.url.includes('sec_return_to_profile')) {
      headers['Delegate-owner'] = user.delegateUserDetails.username;
    }

    // Add only if exists
    if (subClientId) {
      headers['SUB-CLIENT-ID'] = subClientId;
    }

    return headers;
  }

  /**
   * Add legacy system headers (session, CSRF, delegate, etc.)
   */
  private addLegacyHeaders(req: HttpRequest<any>, token?: string): HttpRequest<any> {
    const headers: { [name: string]: string } = {};
    const addHeader = (key: string, value: string | null) => {
      if (value) {
        headers[key] = value;
      }
    };

    if (localStorage.getItem(AppConstant.ACCESS_TOKEN)) {
      addHeader('Authorization', `Bearer ${localStorage.getItem(AppConstant.ACCESS_TOKEN)}`);
    }
    if (localStorage.getItem(AppConstant.SESSION_ID)) {
      addHeader('X-Auth-Token', localStorage.getItem(AppConstant.SESSION_ID));
    }
    if (localStorage.getItem(AppConstant.CSRF_TOKEN)) {
      addHeader('X-CSRF-TOKEN', localStorage.getItem(AppConstant.CSRF_TOKEN));
    }
    if (localStorage.getItem(AppConstant.SUB_CLIENT_ID) && this.privilegeService.isPortal()) {
      addHeader('SUB-CLIENT-ID', localStorage.getItem(AppConstant.SUB_CLIENT_ID));
    }

    const user = JSON.parse(localStorage.getItem(AppConstant.SESSION_USER_ATTR) || '{}');
    if (user?.switchedToDelegateAccount && !req.url.includes('sec_get_user_details_and_nav_list') && !req.url.includes('sec_return_to_profile')) {
      addHeader('Delegate-owner', user.delegateUserDetails.username);
    }

    if (token) {
      addHeader('Authorization', `Bearer ${token}`);
    }

    return req.clone({ setHeaders: headers });
  }

  /**
   * Handle all error responses (shared for both Auth0 and legacy)
   */
  private handleError(error: HttpErrorResponse, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let errorMessage = error.error instanceof ErrorEvent ? error.error.error : error.error;

    if (!req.url.includes('oauth/token') && this.statusToTrack.includes(error.status)) {
      this.commonUtil.logErrorToSentry(error, {
        tags: {
          errorType: AppEnumConstants.API_ERROR,
          url: req.url,
          method: req.method,
          status: error.status.toString(),
        },
        extra: {
          request: {
            url: req.url, method: req.method, body: req.body,
            headers: req.headers.keys().reduce((acc, key) => {
              acc[key] = req.headers.get(key);
              return acc;
            }, {} as Record<string, string>),
          },
          response: { status: error.status, statusText: error.statusText, message: error.message, error: error.error }
        }
      });
    }

    switch (error.status) {
      case AppConstant.FORBIDDEN:
        this.notificationService.errorMessage(HttpResponseMessage.FORBIDDEN_MESSAGE);
        break;
      case AppConstant.DELICATE_NO_ACCESS:
        return this.handleDelicateAccessError(req, next, error);
      case AppConstant.SERVER_DOWN:
      case AppConstant.SERVER_NOT_FOUND:
      case AppConstant.SERVER_TIME_OUT:
        this.notificationService.errorMessage(HttpResponseMessage.SERVER_DOWN_MESSAGE);
        errorMessage = HttpResponseMessage.SERVER_DOWN_MESSAGE;
        break;
      case AppConstant.UN_AUTHORIZED:
        if (this.isAuth0User()) {
          return this.handleAuth0401Error(req, next);
        } else {
          return this.handleLegacy401Error(req, next);
        }
      case AppConstant.BAD_REQUEST:
        if (this.isAuth0User()) {
          this.authOktaService.signOut({ logoutParams: { returnTo: window.location.origin } });
        } else {
          if (error.url.includes('oauth/token')) {
            this.loginLogOutService.logOut(true);
          }
        }
        break;
      case AppConstant.NOT_ACCEPTABLE:
        if (this.isAuth0User()) {
          this.authOktaService.signOut({ logoutParams: { returnTo: window.location.origin } });
        } else {
          if (!req.url.includes('/oauth/token')) {
            this.loginLogOutService.logOut(true);
          }
        }
        break;
    }

    return throwError(() => errorMessage);
  }

  private handleAuth0401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshingAuth0Token) {
      this.isRefreshingAuth0Token = true;
      this.refreshTokenSubject.next(null);

      return from(this.auth0.getAccessTokenSilently()).pipe(
        switchMap(token => {
          this.isRefreshingAuth0Token = false;
          this.refreshTokenSubject.next(token);
          const authReq = token ? req.clone({ setHeaders: this.addAuth0Headers(token, req) }) : req;
          return next.handle(authReq);
        }),
        catchError((error) => {
          this.isRefreshingAuth0Token = false;
          if (this.isAuth0TokenError(error)) {
            console.warn('Auth0 refresh token expired or missing during 401 retry, logging out user', error);
            if (!this.isLoggingOut) {
              this.isLoggingOut = true;
              this.loginLogOutService.clearLocalStorage(true);
              this.authOktaService.signOut({ logoutParams: { returnTo: window.location.origin } });
            }
          } else {
            // For other errors, ensure we sign out 
            this.authOktaService.signOut({ logoutParams: { returnTo: window.location.origin } });
          }
          return EMPTY;
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          const headers = this.addAuth0Headers(token, req);
          const authReq = token ? req.clone({ setHeaders: headers }) : req;
          return next.handle(authReq);
        })
      );
    }
  }

  /**
   * Handle legacy 401 with refresh token
   */
  private handleLegacy401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!req.url.includes('oauth/token') && localStorage.getItem(AppConstant.REFRESH_TOKEN)) {
      if (!this.tokenRevalidateService.refreshToken) {
        this.refreshTokenSubject.next(null);
        this.tokenRevalidateService.refreshToken = true;

        return this.tokenRevalidateService.refreshAccessToken().pipe(
          catchError(error => {
            this.tokenRevalidateService.refreshToken = false;
            this.loginLogOutService.logOut(true);
            return throwError(() => error);
          }),
          switchMap((res: any) => {
            const token = res.body.access_token;
            if (token) {
              this.refreshTokenSubject.next(token);
              this.tokenRevalidateService.refreshToken = false;
              this.tokenRevalidateService.setLoginProperties(res, true, true);
              return next.handle(this.addLegacyHeaders(req, token));
            }
            this.loginLogOutService.logOut(true);
            return EMPTY;
          })
        );
      }
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.addLegacyHeaders(req, token)))
      );
    } else {
      this.loginLogOutService.logOut(true);
      return EMPTY;
    }
  }

  private addTokenHeader(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });
  }

  private handleDelicateAccessError(req: HttpRequest<any>, next: HttpHandler, errorRes: HttpErrorResponse): Observable<HttpEvent<any>> {
    if (!this.isHandlingDelicateError || req.url.includes('sec_get_user_details_and_nav_list')) {
      this.isHandlingDelicateError = true;
      this.delicateErrorSubject.next(true);
      const user = JSON.parse(localStorage.getItem(AppConstant.SESSION_USER_ATTR));
      const id = (errorRes?.error.id ?? '');
      const username = (errorRes?.error.username ?? '');
      let toDelicate = user && this.privilegeService.isSwitchedToDelicate() ? AppConstant.STATUS_NO : AppConstant.STATUS_YES;
      if (this.privilegeService.getCurrentDelicateUserDetails() &&
        errorRes.error.username !== this.privilegeService.getCurrentDelicateUserDetails().username) {
        toDelicate = AppConstant.STATUS_YES;
      }
      const promise = this.getUserDetailsAndNavList(id, toDelicate, username);

      return defer(() =>
        from(promise.then(async (res: any) => {
          this.isHandlingDelicateError = false;
          this.delicateErrorSubject.next(false);
          await this.tokenRevalidateService.setAuthenticatedDelegateAccount(res.body.navContainerDto, res.body.loginUser, errorRes);
          return null;
        })).pipe(
          switchMap(() => EMPTY),
          catchError((error) => {
            this.isHandlingDelicateError = false;
            this.delicateErrorSubject.next(false);
            return throwError(error);
          })
        )
      );
    } else {
      return EMPTY;
    }
  }

  private getUserDetailsAndNavList(id: string, toDelegate: string, username: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.userService.getUserDetailsAndNavList(id, toDelegate, username).subscribe(
        res => resolve(res),
        error => reject(error)
      );
    });
  }


}
